import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/components/podkit/lib/cn";
import { getEnvironmentClassStatus, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { memo, type FC } from "react";
import { type LucideIcon, GripVertical, X } from "lucide-react";
import { WarmPoolExpandedRow } from "@/components/projects/WarmPoolExpandedRow";
import { WARM_POOL_DEFAULT_SIZE, type WarmPoolConfig } from "@/queries/warm-pool-queries";

type EnvironmentClassStatusProps = {
    offline: boolean;
    policyRestricted: boolean;
    degraded: boolean;
};

const EnvironmentClassStatus: FC<EnvironmentClassStatusProps> = ({ offline, policyRestricted, degraded }) => {
    let message: string | undefined = undefined;
    if (offline) {
        message = "Offline";
    } else if (policyRestricted) {
        message = "Policy restricted";
    } else if (degraded) {
        message = "Degraded";
    }

    if (!message) {
        return null;
    }
    return <span className="text-right text-sm text-content-tertiary">{message}</span>;
};

type EnvironmentClassItemProps = {
    id: string;
    environmentClassEntry: EnvironmentClassEntry;
    disabled?: boolean;
    isDraggable?: boolean;
    icon?: LucideIcon;
    className?: string;
    onRemove?: () => void;
    showPrebuildCheckbox?: boolean;
    isPrebuildSelected?: boolean;
    onPrebuildToggle?: (checked: boolean) => void;
    warmPoolConfig?: WarmPoolConfig;
};

export const EnvironmentClassItem: FC<EnvironmentClassItemProps> = memo(
    ({
        id,
        environmentClassEntry,
        disabled,
        isDraggable,
        icon: Icon,
        className,
        onRemove,
        showPrebuildCheckbox,
        isPrebuildSelected,
        onPrebuildToggle,
        warmPoolConfig,
    }) => {
        const sortable = useSortable({
            id,
            disabled: !isDraggable || disabled,
        });

        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

        const style = isDraggable
            ? {
                  transform: CSS.Transform.toString(transform),
                  transition,
              }
            : {};

        const { policyRestricted, offline, degraded } = getEnvironmentClassStatus(environmentClassEntry);

        // Derive warm pool state for this specific environment class
        const envClassId = environmentClassEntry.clazz.id;
        const warmPool = warmPoolConfig?.warmPoolsByEnvClass?.get(envClassId);
        const localState = warmPoolConfig?.warmPoolLocalState.get(envClassId);
        const showWarmPoolRow = !!warmPoolConfig && isPrebuildSelected;

        return (
            <div ref={setNodeRef} style={style} className={cn("flex flex-col", className)}>
                <div
                    className={cn(
                        "flex w-full flex-row items-center overflow-x-hidden border-border-light px-4 py-2 transition-colors",
                        isDraggable && !disabled && "cursor-grab hover:bg-surface-hover active:cursor-grabbing",
                        isDragging && "opacity-50",
                        disabled && "cursor-not-allowed opacity-50",
                    )}
                    {...(isDraggable && !disabled ? attributes : {})}
                    {...(isDraggable && !disabled ? listeners : {})}
                >
                    {isDraggable && (
                        <div className="flex items-center justify-center pr-2 text-content-secondary">
                            <GripVertical size={16} />
                        </div>
                    )}

                    <div className="flex w-full min-w-0 flex-1 items-center justify-between">
                        <div
                            className={cn(
                                "flex w-full flex-col items-start",
                                (offline || policyRestricted || degraded) && "max-w-[85%]",
                            )}
                        >
                            <div className="flex w-full gap-1">
                                <span
                                    title={environmentClassEntry?.clazz?.displayName}
                                    className={cn(
                                        "overflow-hidden text-ellipsis whitespace-nowrap text-base font-bold text-content-primary group-data-[disabled]:text-content-tertiary",
                                    )}
                                >
                                    {environmentClassEntry?.clazz?.displayName}
                                </span>
                                <span
                                    className="overflow-hidden text-ellipsis whitespace-nowrap text-base text-content-tertiary"
                                    title={environmentClassEntry?.runner?.name}
                                >
                                    {environmentClassEntry?.runner?.name}
                                </span>
                            </div>
                            <div className="flex w-full gap-1">
                                <span
                                    className="overflow-hidden text-ellipsis whitespace-nowrap text-left text-sm text-content-tertiary"
                                    title={environmentClassEntry?.clazz?.description}
                                >
                                    {environmentClassEntry?.clazz?.description}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <EnvironmentClassStatus
                                offline={offline}
                                policyRestricted={policyRestricted}
                                degraded={degraded}
                            />
                            {Icon && (
                                <div className="aria-hidden flex h-4 w-4 items-center justify-center text-content-secondary">
                                    <Icon size={16} />
                                </div>
                            )}
                        </div>
                    </div>

                    {showPrebuildCheckbox && (
                        <div className="flex items-center justify-center px-2">
                            <input
                                type="checkbox"
                                checked={isPrebuildSelected || false}
                                onChange={(e) => onPrebuildToggle?.(e.target.checked)}
                                disabled={disabled}
                                className="h-4 w-4 rounded border-border-base accent-always-dark"
                                aria-label="Enable prebuild for this environment class"
                            />
                        </div>
                    )}

                    {onRemove && (
                        <div className="flex items-center justify-center pl-2">
                            <button
                                disabled={disabled}
                                onClick={onRemove}
                                className="flex h-6 w-6 items-center justify-center rounded text-content-secondary hover:bg-surface-hover hover:text-content-primary"
                                aria-label="Remove environment class"
                                data-tracking-id="remove-environment-class"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    )}
                </div>

                {showWarmPoolRow && warmPoolConfig && (
                    <WarmPoolExpandedRow
                        envClassId={envClassId}
                        warmPoolEnabled={localState?.enabled ?? false}
                        warmPoolSize={localState?.size ?? WARM_POOL_DEFAULT_SIZE}
                        warmPool={warmPool}
                        onWarmPoolToggle={(enabled) => warmPoolConfig.onToggle(envClassId, enabled)}
                        onWarmPoolSizeChange={(size) => warmPoolConfig.onSizeChange(envClassId, size)}
                        onLocalSizeChange={(size) => warmPoolConfig.onLocalSizeChange(envClassId, size)}
                        disabled={disabled}
                        isMutating={localState?.mutating}
                    />
                )}
            </div>
        );
    },
);

EnvironmentClassItem.displayName = "EnvironmentClassItem";
